import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AvatarCell, avatarToneFromSeed } from '@bloomays-lib/ui.shared/datagrid';
import { getPrivateAdminDashboardMissionPath, initialsFromName } from '@bloomays-lib/utils.shared';
import type { MissionContractBlock } from '../../helpers/legalContracts';

export type MissionGroupHeaderProps = {
  block: MissionContractBlock;
  expanded: boolean;
  onToggleExpanded: () => void;
};

const resolveSocietyName = (name: string | null | undefined, missingMarker: string): string | null => {
  if (!name || name === missingMarker) return null;
  return name;
};

/**
 * Mission block header shown as the row-grouping cell in `TableLegalContracts`
 * (rendered through `muiProps.groupingColDef.renderCell`, since `DataGridV2`'s
 * `guardGroup` only shows the raw grouped value on group rows otherwise).
 *
 * Societies are always shown as **bloomer → client**. Missing societies render
 * a styled warning instead of the raw backend sentinel string
 * (`missing*SocietySentinel` in i18n, injected by `formatMission`).
 *
 * Compteurs « X à signer » / « X en retard » / « X lignes » dérivés côté front
 * (`toSignCount`, `lateCount`, `lines.length`).
 *
 * Le libellé de mission est un lien vers la fiche mission
 * (`/missions-freelance/:id`, rendue en V2 via le flag `missionDashboardV2`).
 * `Link` conserve le Cmd/Ctrl+clic « nouvel onglet », et le clic est stoppé pour
 * ne pas déclencher les interactions de la ligne de groupe.
 */
const MissionGroupHeader = ({ block, expanded, onToggleExpanded }: MissionGroupHeaderProps) => {
  const { t } = useTranslation(['legalContracts']);
  const clientSociety = resolveSocietyName(block.clientSocietyName, t('missingClientSocietySentinel'));
  const bloomerSociety = resolveSocietyName(block.bloomerSocietyName, t('missingBloomerSocietySentinel'));
  const lineCount = block.lines.length;

  return (
    <Root>
      <Identity>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpanded();
          }}
          aria-label={expanded ? t('collapseMission') : t('expandMission')}
          aria-expanded={expanded}
        >
          {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        </IconButton>
        <AvatarCell initials={initialsFromName(block.bloomerFullName)} tone={avatarToneFromSeed(block.missionId)} />
        <TextBlock>
          <TitleRow>
            <MissionLabel
              to={getPrivateAdminDashboardMissionPath(block.missionId)}
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
              title={t('openMissionDetails')}
            >
              {block.missionLabel}
            </MissionLabel>
            {block.bloomerFullName && (
              <>
                <Separator aria-hidden="true">·</Separator>
                <BloomerName>{block.bloomerFullName}</BloomerName>
              </>
            )}
          </TitleRow>
          <SubtitleRow>
            <SocietiesRow>
              {bloomerSociety ? <span>{bloomerSociety}</span> : <MissingSociety label={t('missingBloomerSociety')} />}
              <ArrowSeparator aria-hidden="true">→</ArrowSeparator>
              {clientSociety ? (
                <ClientSocietyName>{clientSociety}</ClientSocietyName>
              ) : (
                <MissingSociety label={t('missingClientSociety')} />
              )}
            </SocietiesRow>
            {block.accountManagerName ? (
              <>
                <Separator aria-hidden="true">·</Separator>
                <span>
                  {t('amPrefix')} {block.accountManagerName}
                </span>
              </>
            ) : null}
          </SubtitleRow>
        </TextBlock>
      </Identity>
      <Meta>
        {block.toSignCount > 0 ? (
          <ToSignBadge>
            <ToSignDot aria-hidden />
            {t('missionGroup.toSign', { count: block.toSignCount })}
          </ToSignBadge>
        ) : null}
        {block.lateCount > 0 ? (
          <LateBadge>
            <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
            {t('missionGroup.late', { count: block.lateCount })}
          </LateBadge>
        ) : null}
        <LineCount>{t('missionGroup.lines', { count: lineCount })}</LineCount>
      </Meta>
    </Root>
  );
};

const MissingSociety = ({ label }: { label: string }) => (
  <MissingSocietyLabel>
    <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
    {label}
  </MissingSocietyLabel>
);

export default MissionGroupHeader;

const Root = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  width: '100%',
  minWidth: 0,
  paddingRight: 8,
}));

const Identity = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
  flex: 1,
}));

const TextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  marginLeft: 4,
}));

const TitleRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  minWidth: 0,
}));

const MissionLabel = styled(Link)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.neutral[90],
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const BloomerName = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[70],
  whiteSpace: 'nowrap',
}));

const SubtitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));

const SocietiesRow = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
}));

const ClientSocietyName = styled('span')(() => ({
  fontWeight: 600,
}));

const ArrowSeparator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
  flexShrink: 0,
}));

const Separator = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[40],
}));

const MissingSocietyLabel = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  color: '#C17A00',
  fontWeight: 600,
}));

const Meta = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
  flexShrink: 0,
}));

/** Aligné sur `StatusChip` tone `warning` (« À signer » des volets). */
const ToSignBadge = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  backgroundColor: '#FDFAF0',
  color: '#C17A00',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
}));

const ToSignDot = styled('span')(() => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  backgroundColor: '#C17A00',
  flexShrink: 0,
}));

/** Rouge réservé au retard (même pastille que `DocumentVoletCard`). */
const LateBadge = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  backgroundColor: '#FCE8E6',
  color: '#C0362C',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
}));

const LineCount = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
}));
